import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Mail, Eye, Trash2, Send, Edit3, Save, X, Loader2, FileText,
  CheckCircle2, AlertCircle, Building2, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const formatCHF = (n) => new Intl.NumberFormat('de-CH', { maximumFractionDigits: 2 }).format(Number(n) || 0);

export const AdminPreemptiveDrafts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ offerShares: 0, customMessage: '', pricePerShare: 0 });
  const [previewToken, setPreviewToken] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewMeta, setPreviewMeta] = useState(null);
  const [selectedTokens, setSelectedTokens] = useState(new Set());

  useEffect(() => {
    if (!user || !['admin'].includes(user.role)) {
      navigate('/admin/login');
      return;
    }
    load();
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(apiBase() + '/api/preemptive/admin/drafts', { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Fehler beim Laden');
      setDrafts(d.drafts || []);
      setSelectedTokens(new Set());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (draft) => {
    setEditId(draft.token);
    setEditForm({
      offerShares: draft.offerShares,
      customMessage: draft.customMessage || '',
      pricePerShare: draft.pricePerShare,
    });
  };

  const saveEdit = async (token) => {
    setBusy(true);
    try {
      const r = await fetch(apiBase() + `/api/preemptive/admin/draft/${token}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          offerShares: parseInt(editForm.offerShares, 10) || 0,
          customMessage: editForm.customMessage,
          pricePerShare: parseFloat(editForm.pricePerShare) || 0,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Speichern fehlgeschlagen');
      toast.success('Gespeichert');
      setEditId(null);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const deleteDraft = async (token) => {
    if (!window.confirm('Diesen Entwurf wirklich löschen?')) return;
    setBusy(true);
    try {
      const r = await fetch(apiBase() + `/api/preemptive/admin/draft/${token}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Löschen fehlgeschlagen');
      toast.success('Entwurf gelöscht');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const sendSingle = async (token, email) => {
    if (!window.confirm(`E-Mail an ${email} jetzt versenden?`)) return;
    setBusy(true);
    try {
      const r = await fetch(apiBase() + `/api/preemptive/admin/draft/${token}/send`, {
        method: 'POST',
        credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Versand fehlgeschlagen');
      toast.success(`Versendet an ${email}`);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const sendSelected = async () => {
    const tokens = Array.from(selectedTokens);
    if (!tokens.length) {
      toast.error('Bitte zuerst Entwürfe auswählen');
      return;
    }
    if (!window.confirm(`${tokens.length} ausgewählte E-Mails wirklich versenden?`)) return;
    setBusy(true);
    try {
      const r = await fetch(apiBase() + '/api/preemptive/admin/drafts/send-batch', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tokens }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Versand fehlgeschlagen');
      toast.success(`${d.sent} E-Mails versendet`);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const openPreview = async (token) => {
    setPreviewToken(token);
    setPreviewHtml(null);
    setPreviewMeta(null);
    try {
      const r = await fetch(apiBase() + `/api/preemptive/admin/draft/${token}/preview`, {
        credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Vorschau fehlgeschlagen');
      setPreviewHtml(d.html);
      setPreviewMeta({ subject: d.subject, to: d.to, acceptUrl: d.acceptUrl });
    } catch (e) {
      toast.error(e.message);
      setPreviewToken(null);
    }
  };

  const toggleSelect = (token) => {
    const next = new Set(selectedTokens);
    if (next.has(token)) next.delete(token);
    else next.add(token);
    setSelectedTokens(next);
  };

  const toggleAll = () => {
    if (selectedTokens.size === drafts.length) setSelectedTokens(new Set());
    else setSelectedTokens(new Set(drafts.map((d) => d.token)));
  };

  const groupedByTranche = useMemo(() => {
    const groups = {};
    for (const d of drafts) {
      const key = d.trancheName || d.trancheKey || '—';
      if (!groups[key]) groups[key] = [];
      groups[key].push(d);
    }
    return groups;
  }, [drafts]);

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-[#C4A55A]/20 bg-black/80 backdrop-blur-md sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/shareholders" className="text-gray-400 hover:text-[#C4A55A]" data-testid="back-to-shareholders">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <FileText className="h-6 w-6 text-[#C4A55A]" />
            <h1 className="text-xl font-semibold">Bezugsrechts-Entwürfe</h1>
            <span className="text-xs text-gray-500 ml-2">{drafts.length} Entwürfe</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              data-testid="drafts-reload"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1a1a1a] border border-white/15 text-gray-200 text-sm hover:bg-white/5 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Neu laden
            </button>
            <button
              onClick={sendSelected}
              disabled={busy || !selectedTokens.size}
              data-testid="drafts-send-selected"
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white text-sm font-semibold disabled:opacity-30"
            >
              <Send className="h-4 w-4" />
              Auswahl versenden ({selectedTokens.size})
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {loading ? (
          <div className="text-center text-gray-400 py-20">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
            Lade Entwürfe…
          </div>
        ) : !drafts.length ? (
          <div className="text-center py-20 bg-[#0f0f0f] rounded-xl border border-white/5">
            <FileText className="h-10 w-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 mb-2">Keine offenen Entwürfe vorhanden.</p>
            <Link to="/admin/shareholders" className="text-[#C4A55A] underline text-sm">
              Neues Mailing vorbereiten →
            </Link>
          </div>
        ) : (
          <>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 text-sm text-amber-200">
              💡 Hier sehen Sie alle Entwürfe vor dem Versand. Sie können Aktien-Anzahl & Text
              pro Empfänger anpassen, einzeln eine Vorschau ansehen, einzeln versenden – oder
              mehrere markieren und gemeinsam freigeben.
            </div>

            <div className="mb-3 flex items-center gap-3 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedTokens.size > 0 && selectedTokens.size === drafts.length}
                  onChange={toggleAll}
                  className="accent-[#C4A55A]"
                  data-testid="drafts-toggle-all"
                />
                <span className="text-gray-400">Alle markieren</span>
              </label>
            </div>

            {Object.entries(groupedByTranche).map(([trancheName, items]) => (
              <section key={trancheName} className="mb-8" data-testid={`drafts-group-${items[0]?.trancheKey}`}>
                <h2 className="text-sm uppercase tracking-wider text-[#C4A55A] mb-3 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  {trancheName} · {items.length} Entwürfe
                </h2>
                <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden">
                  {items.map((draft) => (
                    <div
                      key={draft.token}
                      data-testid={`draft-row-${draft.shareholderPk}`}
                      className="border-b border-white/5 last:border-b-0 p-4 hover:bg-white/[0.02]"
                    >
                      <div className="flex flex-wrap items-start gap-4">
                        <input
                          type="checkbox"
                          checked={selectedTokens.has(draft.token)}
                          onChange={() => toggleSelect(draft.token)}
                          className="mt-1 accent-[#C4A55A]"
                          data-testid={`draft-select-${draft.shareholderPk}`}
                        />

                        <div className="flex-1 min-w-64">
                          <div className="font-medium">{draft.fullName}</div>
                          <div className="text-sm text-gray-400">{draft.email}</div>
                        </div>

                        {editId === draft.token ? (
                          <div className="flex-1 min-w-72 grid grid-cols-2 gap-2">
                            <label className="block">
                              <span className="text-[11px] text-gray-500 uppercase tracking-wider">Aktien</span>
                              <input
                                type="number"
                                min="0"
                                value={editForm.offerShares}
                                onChange={(e) => setEditForm({ ...editForm, offerShares: e.target.value })}
                                className="w-full bg-black/60 border border-white/15 rounded px-2 py-1 text-white text-sm"
                                data-testid={`edit-shares-${draft.shareholderPk}`}
                              />
                            </label>
                            <label className="block">
                              <span className="text-[11px] text-gray-500 uppercase tracking-wider">Preis CHF</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editForm.pricePerShare}
                                onChange={(e) => setEditForm({ ...editForm, pricePerShare: e.target.value })}
                                className="w-full bg-black/60 border border-white/15 rounded px-2 py-1 text-white text-sm"
                              />
                            </label>
                            <label className="col-span-2 block">
                              <span className="text-[11px] text-gray-500 uppercase tracking-wider">Zusatztext</span>
                              <textarea
                                rows={2}
                                value={editForm.customMessage}
                                onChange={(e) => setEditForm({ ...editForm, customMessage: e.target.value })}
                                className="w-full bg-black/60 border border-white/15 rounded px-2 py-1 text-white text-sm"
                              />
                            </label>
                          </div>
                        ) : (
                          <div className="flex-1 min-w-72 grid grid-cols-3 gap-3 text-sm">
                            <div>
                              <div className="text-[11px] text-gray-500 uppercase tracking-wider">Aktien</div>
                              <div className="font-semibold tabular-nums text-[#C4A55A]">
                                {draft.offerShares?.toLocaleString('de-CH')}
                              </div>
                            </div>
                            <div>
                              <div className="text-[11px] text-gray-500 uppercase tracking-wider">Preis / Aktie</div>
                              <div className="tabular-nums">CHF {formatCHF(draft.pricePerShare)}</div>
                            </div>
                            <div>
                              <div className="text-[11px] text-gray-500 uppercase tracking-wider">Gesamt</div>
                              <div className="font-semibold tabular-nums">CHF {formatCHF(draft.totalCost)}</div>
                            </div>
                            {draft.customMessage && (
                              <div className="col-span-3 text-xs text-gray-400 italic line-clamp-2">
                                „{draft.customMessage}"
                              </div>
                            )}
                          </div>
                        )}

                        <div className="flex items-center gap-1 flex-wrap">
                          <ActionBtn
                            icon={Eye}
                            label="Vorschau"
                            onClick={() => openPreview(draft.token)}
                            testId={`draft-preview-${draft.shareholderPk}`}
                          />
                          {editId === draft.token ? (
                            <>
                              <ActionBtn
                                icon={Save}
                                label="Speichern"
                                color="emerald"
                                onClick={() => saveEdit(draft.token)}
                                disabled={busy}
                                testId={`draft-save-${draft.shareholderPk}`}
                              />
                              <ActionBtn
                                icon={X}
                                label=""
                                onClick={() => setEditId(null)}
                              />
                            </>
                          ) : (
                            <ActionBtn
                              icon={Edit3}
                              label="Bearbeiten"
                              onClick={() => startEdit(draft)}
                              testId={`draft-edit-${draft.shareholderPk}`}
                            />
                          )}
                          <ActionBtn
                            icon={Send}
                            label="Senden"
                            color="emerald"
                            onClick={() => sendSingle(draft.token, draft.email)}
                            disabled={busy}
                            testId={`draft-send-${draft.shareholderPk}`}
                          />
                          <ActionBtn
                            icon={Trash2}
                            label=""
                            color="red"
                            onClick={() => deleteDraft(draft.token)}
                            disabled={busy}
                            testId={`draft-delete-${draft.shareholderPk}`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </main>

      {/* Preview modal */}
      {previewToken && (
        <div
          className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="preview-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setPreviewToken(null); }}
        >
          <div className="bg-white text-black rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500">E-Mail an</div>
                <div className="font-semibold">{previewMeta?.to}</div>
                {previewMeta?.subject && (
                  <div className="text-sm text-gray-600 mt-1">Betreff: {previewMeta.subject}</div>
                )}
              </div>
              <button
                onClick={() => setPreviewToken(null)}
                className="text-gray-500 hover:text-black text-2xl leading-none"
                data-testid="preview-close"
              >×</button>
            </div>
            <div className="flex-1 overflow-y-auto bg-gray-100">
              {previewHtml ? (
                <iframe
                  title="E-Mail Vorschau"
                  srcDoc={previewHtml}
                  className="w-full h-[70vh] bg-white border-0"
                  data-testid="preview-iframe"
                />
              ) : (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ActionBtn = ({ icon: Icon, label, onClick, color, disabled, testId }) => {
  const cls = {
    emerald: 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25',
    red: 'bg-red-500/10 text-red-300 hover:bg-red-500/25',
  }[color] || 'bg-white/5 text-gray-300 hover:bg-white/10';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium disabled:opacity-40 ${cls}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
};
